<template>
  <div class="chat-list-wrapper">
    <el-row v-loading="fetchQueueChatLoading">
      <el-row
        class="chat-list-item"
        v-for="(chat, index) in leadPriorityChatsList"
        :key="`${chat.PartitionKey}-${chat.RowKey}`"
        @click.native="selectChat(chat.RowKey, chat.interactions?.length || 0)"
      >
        <el-badge
          :class="getChatClass(chat.RowKey, chat, selectedChatId)"
          :value="chat.unreadMessage"
          :hidden="chat.unreadMessage === 0"
          style="display: block; width: 100%"
        >
          <el-row style="margin-bottom: 0; width: 100%">
            <el-col style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">
              <el-row type="flex" style="margin-bottom: 0; align-items: center">
                <!-- <ChatAvatar :chat="chat" style="display: inline-block" /> -->
                <el-col
                  style="
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                  "
                >
                  <div class="chat-name">
                    <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">
                      {{ getChatName(chat) }}
                    </span>
                  </div>
                  <el-row
                    type="flex"
                    justify="space-between"
                    style="margin-bottom: 0; margin-top: 4px; overflow: hidden"
                  >
                    <CustomColorTag
                      :color="getTagColor(getTagName(chat.stateVariables))"
                      v-if="getTagName(chat.stateVariables)"
                      size="mini"
                      >{{ getTagName(chat.stateVariables) }}</CustomColorTag
                    >
                    <div class="chat-item-meta-second-row">
                      {{ waitedTime[index] }}
                    </div>
                  </el-row>
                </el-col>
              </el-row>
            </el-col>
            <el-col
              v-if="Array.isArray(chat.agents) && chat.agents.length > 0"
              :span="24"
              class="has-text-dark"
              style="font-size: 0.6em; margin-top: 5px; white-space: normal; word-wrap: break-word; font-weight: 400"
            >
              <i>Attended by {{ chat.agents.join(", ") }}</i>
            </el-col>
          </el-row>
        </el-badge>
        <!-- </el-row> -->
      </el-row>

      <!-- Is Empty -->
      <p v-if="!chatsList || chatsList.length === 0">
        {{
          isUnassignedChatList
            ? "There are no users who need your help at the moment."
            : "You are not attending to any users at the moment."
        }}
      </p>
    </el-row>
  </div>
</template>

<script>
/**
 * QueueChatsList - Live chat list display component.
 *
 * @component
 * @description Renders a list of live chats with wait time, unread badges, tags, and agent assignments.
 * Displays chats sorted by queue time (oldest first). Plays sound notifications for new messages
 * and waiting assignments when enabled. Shows different empty states for assigned vs unassigned lists.
 *
 * @prop {Array} chatsList - Array of chat objects to display
 * @prop {Number} selectedIndex - Currently selected chat index (synced)
 * @prop {Boolean} closed - Mobile view closed state (synced)
 * @prop {String} name - List identifier ('unassignedChatList' for unassigned chats)
 *
 * @fires on-init-fetch-interaction - Emits when chat is selected and needs interaction history
 * @fires update:closed - Syncs mobile view closed state
 *
 * @example
 * <QueueChatsList
 *   :chats-list="queueChats"
 *   :selected-index.sync="selectedIndex"
 *   @on-init-fetch-interaction="fetchInteractions"
 * />
 */
import moment from "moment";
import _ from "lodash";
import { simplifyMessage } from "@/helperMethods/livechat/simplifyMessage";
import ChatAvatar from "@/components/LiveChat/ChatAvatar";
import CustomColorTag from "@/components/LiveChat/CustomColorTag";
import { mapGetters } from "vuex";
import { getChatName, getTagName, tagType, getTagColor } from "@/filters";
import {
  getAvatar,
  getChatClass,
  getDisplayId,
  getPreview,
  printDuration,
} from "@/helperMethods/livechat/util";
import notificationUnreadMessage from "../../../static/mixkit-happy-bell-alert-601.wav";
export default {
  name: "ChatsList",
  components: {
    ChatAvatar,
    CustomColorTag,
  },
  props: {
    chatsList: Array,
    selectedIndex: Number,
    closed: Boolean,
    name: String,
  },
  data() {
    return {
      waitedTime: [],
      tab: "queue",
      unreadMessage: 0,
      isUserOnSite: true,
      isVisible: true,
      waitingForAssignment: 0,
    };
  },
  computed: {
    ...mapGetters(["fetchQueueChatLoading", "selectedChatId", "selectedChat", "queueChatsArray"]),
    isUnassignedChatList() {
      return this.name === "unassignedChatList";
    },
    isUnassignedChatListEmpty() {
      return this.chatsList.length === 0;
    },
    leadPriorityChatsList() {
      if (this.chatsList) {
        const sortedList = _.sortBy(this.chatsList, (chat) => {
          return moment(chat.startQueueTime).valueOf();
        });
        return sortedList;
      } else {
        return [];
      }
    },
    listUnassignedChats() {
      if (this.chatsList) {
        return _.filter(this.chatsList, (chat) => {
          return chat.waiting_for_assignment == true;
        });
      } else {
        return [];
      }
    },
  },

  methods: {
    getAvatar,
    getDisplayId,
    getChatClass,
    getPreview,
    printDuration,
    getChatName,
    getTagName,
    tagType,
    getTagColor,
    selectChat(chatId, interactionsLength) {
      this.$store.commit("SET_SELECTED_CHAT_ID", chatId);
      this.$store.dispatch("RESET_UNREAD_MESSAGE", chatId);
      this.$emit("update:closed", true);
      if (interactionsLength < 10) {
        this.$emit("on-init-fetch-interaction");
      }
    },
    getSessionHistory(chat) {
      if (chat.interactions) {
        const chatInteractions = _.orderBy(
          chat.interactions,
          [
            (interaction) => {
              return moment(interaction.date_created).valueOf();
            },
          ],
          ["asc"]
        );
        const afterSlice = _.slice(chatInteractions, chatInteractions.length - 15);
        const sessionHistory = _.chain(afterSlice).map(simplifyMessage).compact().value();
        return sessionHistory;
      }
      return [];
    },
    calculateWaitedTime() {
      this.waitedTime = _.map(this.leadPriorityChatsList, (chat) => {
        return this.printDuration(chat.startQueueTime, moment().format());
      });
    },
    playSound() {
      const audio = new Audio(notificationUnreadMessage);
      audio.play();
    },
    handleWindowBlur: function () {
      this.isUserOnSite = false;
    },
    handleWindowFocus: function () {
      this.isUserOnSite = true;
    },
    handleVisibilityChange: function () {
      if (document.visibilityState === "visible") {
        this.isVisible = true;
      } else {
        this.isVisible = false;
      }
    },
  },
  // watch for changes in the array leadPriorityChatsList if leadPriorityChatsList[i].unreadMessage change
  // then play a sound
  watch: {
    listUnassignedChats: {
      handler: function (newVal) {
        if (!_.get(this, "$store.state.modules.webchat.soundNotification")) {
          return;
        }
        if (newVal.length > 0 || (newVal.length == 0 && this.waitingForAssignment > 0)) {
          if (newVal.length > this.waitingForAssignment) {
            const previousWaitingForAssignment = this.waitingForAssignment;
            this.waitingForAssignment = newVal.length;
            if (previousWaitingForAssignment == 0 && newVal.length > 1) {
              return;
            }
            this.playSound();
          }
          if (newVal.length < this.waitingForAssignment) {
            this.waitingForAssignment = newVal.length;
          }
        }
      },
      deep: true,
    },
    leadPriorityChatsList: {
      handler: function (newVal) {
        if (!_.get(this, "$store.state.modules.webchat.soundNotification")) {
          return;
        }
        if (this.isUserOnSite && !this.isVisible) {
          return;
        }
        if (newVal && newVal.length > 0) {
          for (let i = 0; i < newVal.length; i++) {
            if (newVal[i].status == "attending") {
              if (newVal[i].unreadMessage > 0 && this.unreadMessage != newVal[i].unreadMessage) {
                this.playSound();
                this.unreadMessage = newVal[i].unreadMessage;
                break;
              }
            }
          }
        }
      },
      deep: true,
    },
  },
  created() {
    window.addEventListener("blur", this.handleWindowBlur);
    window.addEventListener("focus", this.handleWindowFocus);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },
  mounted() {
    this.calculateWaitedTimeinterval = setInterval(() => {
      this.calculateWaitedTime();
    }, 10000);
  },
  beforeDestroy() {
    clearInterval(this.calculateWaitedTimeinterval);
    window.removeEventListener("blur", this.handleWindowBlur);
    window.removeEventListener("focus", this.handleWindowFocus);
    window.removeEventListener("visibilitychange", this.handleVisibilityChange);
  },
};
</script>

<style scoped lang="scss">
@import "./styles/chat-list.scss";
</style>
